import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import TornPaperWrapper from '../components/TornPaperWrapper';
import api from '../services/api';
import toast from '../utils/toast';
import { trackPurchase, identifyUser } from '../utils/metaPixel';
import { trackGaPurchase } from '../utils/ga4';
import './SuccessReplica.css';
import { getCmsImagePath } from '../cms/cmsData';

// Upload pending vendor documents from IndexedDB (stored before Stripe checkout)
const uploadPendingVendorDocuments = async (email) => {
  if (!email) return;
  const emailLower = email.toLowerCase();
  console.log('[VendorPending] Checking IndexedDB for pending vendor doc uploads, email:', emailLower);

  try {
    const dbRequest = indexedDB.open('VendorDocuments', 1);
    dbRequest.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('pendingUploads')) {
        db.createObjectStore('pendingUploads', { keyPath: 'email' });
      }
    };
    dbRequest.onerror = (e) => console.error('[VendorPending] IndexedDB open error:', e.target.error);
    dbRequest.onsuccess = async (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('pendingUploads')) {
        console.log('[VendorPending] No pendingUploads store — no pending docs to upload');
        db.close();
        return;
      }
      const tx = db.transaction(['pendingUploads'], 'readonly');
      const store = tx.objectStore('pendingUploads');
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = async () => {
        const allEntries = getAllRequest.result || [];
        console.log(`[VendorPending] Found ${allEntries.length} IndexedDB entries`);
        if (allEntries.length === 0) return;

        // Try exact email match first, then fallback to most recent entry
        let data = allEntries.find(e => e.email && e.email.toLowerCase() === emailLower);
        if (!data) {
          data = allEntries.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];
          if (data) console.log(`[VendorPending] No exact match, using most recent entry: ${data.email}`);
        }

        if (!data || !data.files || Object.keys(data.files).length === 0) {
          console.log('[VendorPending] No pending documents found');
          return;
        }

        const documentTypeMap = {
          sellerPermit: 'SELLER_PERMIT',
          businessLicense: 'BUSINESS_LICENSE',
          healthPermit: 'HEALTH_PERMIT',
          foodHandler: 'FOOD_HANDLER'
        };

        const fileKeys = Object.keys(data.files).filter(k => documentTypeMap[k]);
        console.log('[VendorPending] Uploading pending documents:', fileKeys);
        toast.info(`Uploading ${fileKeys.length} pending document(s)...`);

        let successCount = 0;
        let failCount = 0;

        for (const [key, fileData] of Object.entries(data.files)) {
          if (fileData && documentTypeMap[key]) {
            try {
              const file = new File(
                [fileData.data],
                fileData.name,
                { type: fileData.type, lastModified: fileData.lastModified }
              );
              console.log(`[VendorPending] Uploading ${key}: ${file.name} (${file.size} bytes)`);
              const result = await api.uploadVendorDocument(documentTypeMap[key], file);
              if (result?.success) {
                console.log(`[VendorPending] Successfully uploaded ${key}`);
                successCount++;
              } else {
                console.error(`[VendorPending] Upload failed for ${key}:`, result);
                failCount++;
              }
            } catch (err) {
              console.error(`[VendorPending] Error uploading ${key}:`, err);
              failCount++;
            }
          }
        }

        if (successCount > 0) toast.success(`Uploaded ${successCount} document(s) successfully!`);
        if (failCount > 0) toast.error(`${failCount} document(s) failed to upload. Upload them from your Dashboard.`);

        // Clear IndexedDB
        try {
          const delReq = indexedDB.open('VendorDocuments', 1);
          delReq.onsuccess = (e) => {
            const delDb = e.target.result;
            const delTx = delDb.transaction(['pendingUploads'], 'readwrite');
            delTx.objectStore('pendingUploads').delete(data.email);
            console.log('[VendorPending] Cleared IndexedDB pending uploads');
          };
        } catch (cleanupErr) {
          console.error('[VendorPending] Cleanup error:', cleanupErr);
        }
      };

      getAllRequest.onerror = (e) => console.error('[VendorPending] getAll error:', e.target.error);
    };
  } catch (err) {
    console.error('[VendorPending] IndexedDB error:', err);
  }
};

const VendorPending = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        // Clear cart after successful vendor checkout
        localStorage.removeItem('cart');
        localStorage.removeItem('pendingCheckoutCart');
        localStorage.removeItem('pendingCart');
        window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: [], total: 0 } }));

        const response = await api.getOrderBySessionId(sessionId);
        if (response?.success && response?.data) {
          setOrderData(response.data.order);

          // ── Meta Pixel: Purchase event with value + currency ──
          const vpOrder = response.data.order;
          const vpTotal = (vpOrder.total_cents || 0) / 100;
          const vpEmail = response.data.stripe_email || vpOrder.buyer?.email;
          const vpName = vpOrder.buyer?.full_name || '';
          const vpParts = vpName.split(' ');
          identifyUser({ email: vpEmail, phone: vpOrder.buyer?.phone, firstName: vpParts[0], lastName: vpParts.slice(1).join(' ') });
          if (vpTotal > 0) {
            trackPurchase({
              value: vpTotal,
              currency: 'USD',
              contentName: 'Vendor Booth',
              numItems: 1,
              orderNumber: vpOrder.order_number,
              userData: { email: vpEmail, phone: vpOrder.buyer?.phone, firstName: vpParts[0], lastName: vpParts.slice(1).join(' ') },
            });

            trackGaPurchase({
              orderNumber: vpOrder.order_number,
              value: vpTotal,
              currency: 'USD',
              tax: (vpOrder.tax_cents || 0) / 100,
              items: [
                {
                  id: vpOrder.id,
                  item_name: 'Vendor Booth',
                  item_category: 'Vendor Booth',
                  price: vpTotal,
                  quantity: 1,
                }
              ],
              search: searchParams.toString() ? `?${searchParams.toString()}` : '',
              purchaseType: 'vendor_booth',
              ticketType: 'vendor',
            });
          }

          // Upload pending vendor documents from IndexedDB immediately after checkout
          const buyerEmail = response.data.order?.buyer?.email;
          const stripeEmail = response.data.stripe_email;
          const vendorBooth = response.data.order?.metadata?.vendor_booths?.[0];
          const vendorEmail = vendorBooth?.vendor_email;
          // Try all possible emails — vendor form email, buyer email, stripe email (deduplicated)
          const emailsToTry = [...new Set([vendorEmail, buyerEmail, stripeEmail].filter(Boolean).map(e => e.toLowerCase()))];
          console.log('[VendorPending] Attempting doc upload with emails:', emailsToTry);
          for (const email of emailsToTry) {
            await uploadPendingVendorDocuments(email);
          }
        } else {
          setError('Could not load order details');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Could not load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [searchParams]);

  const handleDownloadInvoice = () => {
    if (!orderData) return;
    const invoiceWindow = window.open('', '_blank');
    if (!invoiceWindow) return;

    const orderDate = orderData.created_at ? new Date(orderData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
    const subtotalCents = orderData.metadata?.original_subtotal_cents || orderData.subtotal_cents || 0;
    const subtotal = subtotalCents / 100;
    const tax = (orderData.tax_cents || 0) / 100;
    const total = (orderData.total_cents || 0) / 100;
    const items = orderData.items || [];
    const isVendorOrder = orderData.metadata?.vendor_booths?.length > 0;
    const vendorBooth = isVendorOrder ? orderData.metadata.vendor_booths[0] : null;
    const vendorLegalName = vendorBooth?.legal_name || vendorBooth?.legal_business_name || vendorBooth?.business_name || '';
    const vendorFullName = vendorBooth?.vendor_name || orderData.buyer?.full_name || '';
    const vendorPhone = vendorBooth?.vendor_phone || vendorBooth?.phone || orderData.buyer?.phone || '';
    const vendorEmail = vendorBooth?.vendor_email || orderData.buyer?.email || '';
    const buyerEmail = vendorEmail || orderData.buyer?.email || '';
    const paymentMethod = orderData.card_brand ? `${orderData.card_brand} ****${orderData.card_last4}` : 'Credit Card';
    const isRefunded = orderData.status === 'REFUNDED' || orderData.status === 'PARTIALLY_REFUNDED';

    // Build Bill To section
    let billToHtml = '';
    if (isVendorOrder && vendorLegalName) {
      billToHtml = `<strong>${vendorFullName || vendorLegalName}</strong>`;
      billToHtml += `<br>${vendorLegalName}`;
      if (vendorPhone) billToHtml += `<br>${vendorPhone}`;
      billToHtml += `<br>${buyerEmail}`;
    } else {
      billToHtml = `<strong>${vendorFullName || orderData.buyer?.full_name || ''}</strong>`;
      if (vendorPhone) billToHtml += `<br>${vendorPhone}`;
      billToHtml += `<br>${buyerEmail}`;
    }

    // Build enhanced item rows with AMP ticket details (section/row/seat)
    const buildItemDesc = (item) => {
      let name = item.name || item.ticket_type_name || 'Item';
      const nameLower = name.toLowerCase();
      if (nameLower.includes('amphitheater') || nameLower.includes('amp')) {
        const ampItems = orderData.metadata?.amphitheater_items || [];
        const allSeats = [];
        ampItems.forEach(ai => {
          (ai.seatDetails || []).forEach(sd => {
            allSeats.push(`${sd.section || ''} Row ${sd.row || ''}, Seat ${sd.number || ''}`);
          });
        });
        if (allSeats.length > 0) {
          name += `<br><span style="font-size:12px;color:#6b7280">${allSeats.join('; ')}</span>`;
        }
      }
      return name;
    };

    const itemRows = items.length > 0
      ? items.map((item, i) => {
          const name = buildItemDesc(item);
          const qty = item.quantity || 1;
          const unitPrice = (item.unit_price_cents || 0) / 100;
          return `<tr>
            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:14px">${i + 1}</td>
            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:14px">${qty}x ${name}</td>
            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:center;font-size:14px">${qty}</td>
            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-size:14px">$${unitPrice.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
            <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-size:14px;font-weight:600">$${(unitPrice * qty).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          </tr>`;
        }).join('')
      : `<tr>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:14px">1</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;font-size:14px">Order Items</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:center;font-size:14px">1</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-size:14px">$${subtotal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-size:14px;font-weight:600">$${subtotal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
        </tr>`;

    const couponCode = orderData.metadata?.coupon_code;
    const discountCents = orderData.metadata?.discount_cents || 0;

    invoiceWindow.document.write(`<!DOCTYPE html><html><head><title>Invoice - ${orderData.order_number}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color:#1a1a1a; background:#f8fafc; padding:40px; }
      .invoice { max-width:800px; margin:0 auto; background:#fff; border-radius:12px; box-shadow:0 1px 3px rgba(0,0,0,0.1); overflow:hidden; }
      .header { padding:40px; display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #e5e7eb; }
      .header h1 { font-size:28px; font-weight:700; letter-spacing:0.5px; color:#1a1a2e; }
      .header .brand { font-size:13px; color:#6b7280; margin-top:4px; }
      .header .invoice-meta { text-align:right; font-size:13px; line-height:1.8; color:#374151; }
      .header .invoice-meta .label { opacity:0.7; }
      .body { padding:40px; }
      .parties { display:flex; justify-content:space-between; margin-bottom:32px; gap:40px; }
      .party h3 { font-size:11px; text-transform:uppercase; letter-spacing:1.5px; color:#6b7280; margin-bottom:8px; font-weight:600; }
      .party p { font-size:14px; line-height:1.7; color:#374151; }
      table { width:100%; border-collapse:collapse; margin-bottom:24px; }
      thead th { background:#f9fafb; padding:10px 12px; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:#6b7280; font-weight:600; border-bottom:2px solid #e5e7eb; }
      .totals { display:flex; justify-content:flex-end; }
      .totals-table { width:260px; }
      .totals-table .row { display:flex; justify-content:space-between; padding:6px 0; font-size:14px; color:#374151; }
      .totals-table .row.total { border-top:2px solid #1a1a2e; padding-top:10px; margin-top:6px; font-size:16px; font-weight:700; color:#1a1a2e; }
      .footer { border-top:1px solid #e5e7eb; padding:24px 40px; text-align:center; color:#9ca3af; font-size:12px; line-height:1.8; }
      .status-badge { display:inline-block; padding:4px 12px; border-radius:20px; font-size:12px; font-weight:600; }
      .status-paid { background:#dcfce7; color:#166534; }
      .status-refunded { background:#fee2e2; color:#991b1b; }
      @media print { body { padding:0; background:#fff; } .invoice { box-shadow:none; } }
    </style></head><body>
    <div class="invoice">
      <div class="header">
        <div style="display:flex;align-items:center;gap:16px">
          <img src="${window.location.origin}/logo.webp" alt="OC MENA Festival" style="height:48px;width:auto;border-radius:6px" />
          <div>
            <h1>INVOICE</h1>
            <div class="brand">OC MENA Festival 2026</div>
          </div>
        </div>
        <div class="invoice-meta">
          <div><span class="label">Invoice #:</span> <strong>${orderData.order_number}</strong></div>
          <div><span class="label">Date:</span> ${orderDate}</div>
          <div><span class="label">Status:</span> <span class="status-badge ${isRefunded ? 'status-refunded' : 'status-paid'}">${orderData.status || 'COMPLETED'}</span></div>
        </div>
      </div>
      <div class="body">
        <div class="parties">
          <div class="party">
            <h3>From</h3>
            <p><strong>OC MENA Festival</strong><br>Irvine, CA 92602<br>United States<br>info@ocmenafestival.com</p>
          </div>
          <div class="party">
            <h3>Bill To</h3>
            <p>${billToHtml}</p>
          </div>
        </div>
        <table>
          <thead><tr>
            <th style="width:40px">#</th>
            <th>Description</th>
            <th style="text-align:center;width:60px">Qty</th>
            <th style="text-align:right;width:100px">Unit Price</th>
            <th style="text-align:right;width:100px">Amount</th>
          </tr></thead>
          <tbody>${itemRows}</tbody>
        </table>
        <div class="totals">
          <div class="totals-table">
            <div class="row"><span>Subtotal</span><span>$${subtotal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
            ${couponCode && discountCents > 0 ? `<div class="row" style="color:#16a34a"><span>Coupon (${couponCode})</span><span>-$${(discountCents / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>` : ''}
            ${tax > 0 ? `<div class="row"><span>Tax (7.75%)</span><span>$${tax.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>` : ''}
            <div class="row total"><span>Total</span><span>$${total.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
            ${isRefunded ? `<div class="row" style="color:#dc2626;font-weight:600"><span>Refunded</span><span>-$${total.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>` : ''}
          </div>
        </div>
        <div style="margin-top:32px;padding:16px;background:#f0fdf4;border-radius:8px;border:1px solid #bbf7d0">
          <p style="font-size:13px;color:#166534;margin:0">
            <strong>Payment:</strong> ${paymentMethod}
          </p>
        </div>
      </div>
      <div class="footer">
        <p>Thank you for your purchase!</p>
        <p>OC MENA Festival &mdash; Irvine, CA &mdash; www.ocmenafestival.com</p>
      </div>
    </div>
    <script>window.onload = function() { window.print(); }<${'/'}script>
    </body></html>`);
    invoiceWindow.document.close();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e5e5e5', borderTopColor: '#D42533', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#666', fontSize: 14 }}>Loading your application status...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 40 }}>
          <h2 style={{ color: '#D42533', marginBottom: 12 }}>Something went wrong</h2>
          <p style={{ color: '#666' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <AnnouncementBar />
      
      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="OC MENA Festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          {/* Title */}
          <h1 className="card-title">🎉 Registration Received!</h1>
          <p className="card-subtitle" style={{ fontWeight: 600 }}>
            Thank you for registering as a vendor for OC MENA Festival 2026.
          </p>

          {/* Body text */}
          <div style={{ maxWidth: 600, margin: '0 auto', fontSize: 14, color: '#333', lineHeight: 1.8 }}>
            
            <p>Your application is currently under review. All vendor registrations are subject to approval to ensure a balanced and curated marketplace experience.</p><br/>
            <p>Please note:</p>
            <ul style={{ paddingLeft: 20, margin: '8px 0 16px' }}>
              <li>Approval is not automatic.</li>
              <li>Vendor categories are limited.</li>
              <li>We reserve the right to approve or decline applications at our discretion.</li>
            </ul>
            <p>You will receive an email with your approval status shortly.</p><br/>
            <p>We appreciate your interest in being part of OC MENA Festival and look forward to reviewing your submission.</p>
            <p style={{ fontWeight: 700, color: '#000', marginTop: 16 }}>
              In the event your application is not approved, a full refund will be issued.
            </p>
          </div>

          {/* Download Invoice + Go to Dashboard */}
          <div className="sr-actions">
            <button
              className="sr-outline-btn"
              onClick={handleDownloadInvoice}
            >
              Download Invoice
            </button>
            <button 
              className="sr-dashboard-btn" 
              onClick={() => window.location.href = '/dashboard'}
            >
              Go to Dashboard
            </button>
          </div>

          <div className="sr-divider" />

          {/* Order Summary — exact same layout as SuccessReplica */}
          <div className="sr-bottom-grid">
            <div className="sr-info-left">
              <div className="sr-billing-shipping">
                <div className="sr-block">
                  <h3>Billing Address</h3>
                  <p className="sr-line">{orderData.buyer?.full_name || 'N/A'}</p>
                  {orderData.billing_address && (
                    <>
                      <p className="sr-line">{orderData.billing_address.line1}</p>
                      {orderData.billing_address.line2 && <p className="sr-line">{orderData.billing_address.line2}</p>}
                      <p className="sr-line">{orderData.billing_address.city}, {orderData.billing_address.state} {orderData.billing_address.postal_code}</p>
                      <p className="sr-line">{orderData.billing_address.country}</p>
                    </>
                  )}
                </div>

                <div className="sr-block sr-order-meta">
                  <div className="sr-meta-row">
                    <div>
                      <div className="label">ORDER NUMBER</div>
                      <div className="value">{orderData.order_number}</div>
                    </div>
                  </div>
                  <div className="sr-meta-row">
                    <div>
                      <div className="label">DATE</div>
                      <div className="value">{orderData.created_at ? new Date(orderData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</div>
                    </div>
                  </div>
                  <div className="sr-meta-row">
                    <div>
                      <div className="label">EMAIL</div>
                      <div className="value">{orderData.buyer?.email || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="sr-meta-row">
                    <div>
                      <div className="label">PAYMENT METHOD</div>
                      <div className="value">{orderData.card_brand ? `${orderData.card_brand} ****${orderData.card_last4}` : 'Card'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sr-summary-card">
              <h3>Order Summary</h3>
              <div className="sr-summary-header">
                <span>Product</span>
                <span>Total</span>
              </div>
              {(orderData.items || []).map((item, idx) => {
                const totalPrice = (item.unit_price_cents / 100) * item.quantity;
                if (totalPrice <= 0) return null;
                return (
                  <div key={idx} className="sr-summary-row">
                    <span>{item.name} x{item.quantity}</span>
                    <span>${totalPrice.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                );
              })}
              <div className="sr-summary-row subtotal">
                <span>Subtotal</span>
                <span>${((orderData.metadata?.original_subtotal_cents || orderData.subtotal_cents) / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              {orderData.metadata?.coupon_code && orderData.metadata?.discount_cents > 0 && (
                <div className="sr-summary-row" style={{ color: '#16a34a' }}>
                  <span>Discount ({orderData.metadata.coupon_code} - {orderData.metadata.discount_percent}% off)</span>
                  <span>-${(orderData.metadata.discount_cents / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              )}
              {orderData.tax_cents > 0 && (
                <div className="sr-summary-row">
                  <span>Sales Tax (7.75%)</span>
                  <span>${(orderData.tax_cents / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              )}
              <div className="sr-summary-row total">
                <span>Total</span>
                <span className="sr-total-amount">USD ${(orderData.total_cents / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            </div>
          </div>

        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath("lanterns", "/lanterns.webp")} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VendorPending;
